// Basic type checking utilities
export const isBoolean = (value) => typeof value === 'boolean';
export const isString = (value) => typeof value === 'string';
export const isNumber = (value) => typeof value === 'number';
export const isArray = (value) => Array.isArray(value);
export const isObject = (value) =>
  value !== null && typeof value === 'object' && !isArray(value);

// Built-in field name to label mapping for common fields
export const FIELD_LABELS = {
  accomodationHotelId: 'Accommodation Hotel',
  accomodationExperienceId: 'Accommodation Experience',
  accomodationType: 'Accommodation Type',
  startPositionId: 'Start Position',
  endPositionId: 'End Position',
  startPosition: 'Start Position',
  endPosition: 'End Position',
  inCityId: 'City',
  extraId: 'Extra',
  accreditationId: 'Accreditation',
  experienceThemeId: 'Theme',
  experienceSegmentId: 'Segment',
  // Program-specific field labels
  programExperiences: 'Experiences',
  programIncludedOptions: 'Included Additional services',
  programSuggestedOptions: 'Suggested Additional services',
  alternativeHotels: 'Alternative Hotels',
  pois: 'Points of Interest',
  // Period-specific field labels
  autoConfirmEnabled: 'Auto Confirm Enabled',
  autoConfirmDays: 'Auto Confirm Days',
  minPax: 'Min Pax',
  maxPax: 'Max Pax',
  availableDays: 'Available Days',
};

// Fields that should match by experienceId instead of id
export const EXPERIENCE_ID_MATCH_FIELDS = [
  'programExperiences',
  'programIncludedOptions',
  'programSuggestedOptions',
  'requestExperiences',
];

// Fields that should match by hotelId instead of id
export const HOTEL_ID_MATCH_FIELDS = ['alternativeHotels', 'requestHotels'];

// Fields that should match by tripTemplateId instead of id
export const TEMPLATE_ID_MATCH_FIELDS = ['requestTemplates'];

// Fields to ignore in history diffs
export const IGNORED_FIELDS = ['transferableId', 'transferableType'];

// Compare two objects, optionally ignoring the 'id' field for experience-related matches
export const areItemsEqual = (before, after, ignoreRecordId = false) => {
  if (ignoreRecordId && isObject(before) && isObject(after)) {
    // eslint-disable-next-line no-unused-vars
    const { id: beforeId, ...beforeRest } = before;
    // eslint-disable-next-line no-unused-vars
    const { id: afterId, ...afterRest } = after;
    return JSON.stringify(beforeRest) === JSON.stringify(afterRest);
  }
  return JSON.stringify(before) === JSON.stringify(after);
};

// Format field name to human-readable label
export const formatFieldLabel = (fieldName) => {
  if (FIELD_LABELS[fieldName]) {
    return FIELD_LABELS[fieldName];
  }
  // Convert camelCase to Title Case with spaces
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Parse image data from JSON string
export const parseImageData = (imageData) => {
  if (!imageData || !isString(imageData)) return null;
  try {
    const parsed = JSON.parse(imageData);
    if (parsed?.original) {
      return {
        id: parsed.original.id,
        filename: parsed.original.metadata?.filename,
        mimeType: parsed.original.metadata?.mime_type,
      };
    }
    return null;
  } catch {
    return null;
  }
};

// Strip HTML tags from a string
export const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return html;
  return html.replace(/<[^>]*>/g, '').trim();
};

// Normalize time values by stripping trailing :00 seconds (e.g., "02:00:00" -> "02:00")
export const normalizeTimeValue = (value) => {
  if (!isString(value)) return value;
  // Match time pattern HH:MM:00 and remove :00 seconds
  return value.replace(/^(\d{1,2}:\d{2}):00$/, '$1');
};

// Normalize ID to string for comparison
export const normalizeId = (id) => {
  if (id === null || id === undefined) return null;
  return String(id);
};

// Get item ID from an object
// For certain fields, prefer experienceId or hotelId over id for matching
export const getItemId = (item, fieldName = null) => {
  if (!isObject(item)) return null;

  // For experience-related fields, match by experienceId to properly track changes
  if (
    fieldName &&
    EXPERIENCE_ID_MATCH_FIELDS.includes(fieldName) &&
    item.experienceId !== undefined
  ) {
    return String(item.experienceId);
  }

  // For hotel-related fields, match by hotelId to properly track changes
  if (
    fieldName &&
    HOTEL_ID_MATCH_FIELDS.includes(fieldName) &&
    item.hotelId !== undefined
  ) {
    return String(item.hotelId);
  }

  // For template-related fields, match by tripTemplateId to properly track changes
  if (
    fieldName &&
    TEMPLATE_ID_MATCH_FIELDS.includes(fieldName) &&
    item.tripTemplateId !== undefined
  ) {
    return String(item.tripTemplateId);
  }

  if (item.id !== undefined) {
    return String(item.id);
  }
  return null;
};
